import { GameType, type Game } from '../types/Game'
import { GameListKey, type UseGameListOptions, type UseGameListReturn } from '../types/composables/UseGameList'
import {
    computed, provide, readonly, ref, toValue,
    useClock, useDayjs, useGamesJackpots, useIntervalFn, usePromotionGames,
} from '#imports'
import type { MaybeRefOrGetter } from '#imports'

/**
 * Sets up list-scoped shared state for a collection of GameTiles and
 * provides it to descendants via inject. Call once in any component that
 * renders one or more `<GameTile>` elements; the tiles then resolve their
 * reactive bindings via {@link useGameTile}.
 *
 * Shared state:
 *  - one opt-in/shared `useGamesJackpots()` query for jackpot-capable lists
 *  - one `usePromotionGames()` query for the whole list
 *  - one ticking `now` ref (1s), gated so it only runs while at least one
 *    game in the list has an `opens_at` within the next 30 minutes
 *
 * @param games - Reactive list of games rendered under this scope.
 *
 * @example
 * ```vue
 * <script setup lang="ts">
 * const props = defineProps<{ games: Game[] }>()
 * useGameList(() => props.games)
 * </script>
 * ```
 */
export const useGameList = (
    games: MaybeRefOrGetter<Game[] | undefined>,
    options: UseGameListOptions = {},
): UseGameListReturn => {
    const dayjs = useDayjs()
    const { clock, now: serverNow } = useClock()
    const now = ref(dayjs(clock.value).tz())

    const hasJackpotGames = computed(() => {
        const list = toValue(games) ?? []

        return list.some(game => game.has_jackpot && game.game_type !== GameType.BINGO)
    })

    const shouldFetchJackpots = computed(() => {
        const option = toValue(options.enableJackpots ?? 'auto')

        if (option === true) return true
        if (option === false) return false

        return hasJackpotGames.value
    })

    const jackpotsQuery = useGamesJackpots({ enabled: shouldFetchJackpots })
    const promotionQuery = usePromotionGames()

    const halfHourMs = dayjs.duration({ minutes: 30 }).asMilliseconds()

    const hasActiveCountdown = computed(() => {
        const list = toValue(games) ?? []
        const nowMs = now.value.valueOf()

        return list.some((game) => {
            const opensAt = game.opening_hours?.opens_at
            if (!opensAt) return false
            const diff = dayjs(opensAt).valueOf() - nowMs
            return diff > 0 && diff <= halfHourMs
        })
    })

    // Client-only: starting a real setInterval on the server would pin the
    // request's scope through the interval callback and reproduce the very
    // retention pattern this architecture exists to fix. SSR renders the
    // countdown from the setup-time `now` snapshot, which is correct for a
    // static HTML response.
    //
    // Interval cadence is reactive: 1s while a countdown is active (smooth
    // "opens in M:SS" display), 30s otherwise. The coarse cadence is what
    // lets `hasActiveCountdown` flip to true for a static list whose earliest
    // `opens_at` is more than 30 min out at setup — gating the interval
    // entirely would leave `now` frozen and the transition unreachable.
    if (import.meta.client) {
        const intervalMs = computed(() => hasActiveCountdown.value ? 1000 : 30_000)
        useIntervalFn(() => {
            now.value = dayjs(serverNow()).tz()
        }, intervalMs)
    }

    const context: UseGameListReturn = {
        jackpotsQuery,
        promotionQuery,
        now: readonly(now),
    }

    provide(GameListKey, context)

    return context
}
